import {
  BeakerIcon,
  BoltIcon,
  CheckIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { cn } from '@duck/ui'
import { useTar } from '@/modules'
import { isRtl } from '@/modules/i18n'
import { GuideLastUpdated, IntentRelatedLinks } from './IntentRelatedLinks'

const AI_PRINCIPLE_CARDS = [
  {
    titleKey: 'aiJudgmentPage.principleCards.useTools.title',
    descriptionKey: 'aiJudgmentPage.principleCards.useTools.description',
    icon: BoltIcon,
  },
  {
    titleKey: 'aiJudgmentPage.principleCards.testOutputs.title',
    descriptionKey: 'aiJudgmentPage.principleCards.testOutputs.description',
    icon: BeakerIcon,
  },
  {
    titleKey: 'aiJudgmentPage.principleCards.ownTheDecision.title',
    descriptionKey: 'aiJudgmentPage.principleCards.ownTheDecision.description',
    icon: ShieldCheckIcon,
  },
] as const

const AI_RISK_COLUMNS = [
  {
    titleKey: 'aiJudgmentPage.riskColumns.speedTitle',
    descriptionKey: 'aiJudgmentPage.riskColumns.speedDescription',
  },
  {
    titleKey: 'aiJudgmentPage.riskColumns.tasteTitle',
    descriptionKey: 'aiJudgmentPage.riskColumns.tasteDescription',
  },
  {
    titleKey: 'aiJudgmentPage.riskColumns.contextTitle',
    descriptionKey: 'aiJudgmentPage.riskColumns.contextDescription',
  },
  {
    titleKey: 'aiJudgmentPage.riskColumns.accountabilityTitle',
    descriptionKey: 'aiJudgmentPage.riskColumns.accountabilityDescription',
  },
] as const

const WEAK_PROMPT_ISSUE_KEYS = [
  'aiJudgmentPage.promptComparisonWeakIssue1',
  'aiJudgmentPage.promptComparisonWeakIssue2',
  'aiJudgmentPage.promptComparisonWeakIssue3',
] as const

const STRONG_PROMPT_WIN_KEYS = [
  'aiJudgmentPage.promptComparisonStrongWin1',
  'aiJudgmentPage.promptComparisonStrongWin2',
  'aiJudgmentPage.promptComparisonStrongWin3',
] as const

export const UsingAiPage = () => {
  const { t, locale } = useTar()
  // Most monospace fonts lack Hebrew glyphs and trigger ugly fallbacks; skip font-mono in RTL.
  const promptFontClass = isRtl(locale) ? '' : 'font-mono'

  return (
    <div className="flex flex-1 w-full justify-center">
      <div className="flex-1 min-w-0 max-w-6xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16">
        <section
          className="grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start gap-10 px-6 py-8 md:px-10 md:py-12"
          aria-labelledby="ai-judgment-heading"
        >
          <div className="flex flex-col items-start gap-6 max-w-xl">
            <h1
              id="ai-judgment-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
            >
              {t('aiJudgmentPage.title')}
              <span className="text-accent">.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('aiJudgmentPage.subtitle')}
            </p>
            <GuideLastUpdated isoDate="2026-05-09" />
            <p className=" text-foreground/90 text-lg max-w-3xl leading-relaxed">
              {t('aiJudgmentPage.riskDescription')}
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('aiJudgmentPage.introP2')}
            </p>
          </div>

          <aside className="rounded-xl border border-border bg-card/60 p-6 md:mt-8">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
              {t('aiJudgmentPage.sidebarEyebrow')}
            </p>
            <p className="text-sm leading-7 text-foreground/90">
              {t('aiJudgmentPage.sidebarBody')}
            </p>
          </aside>
        </section>

        <section
          className="space-y-7"
          aria-labelledby="prompt-comparison-heading"
        >
          <div className="space-y-3">
            <h2
              id="prompt-comparison-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('aiJudgmentPage.promptComparisonTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('aiJudgmentPage.promptComparisonDescription')}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-4 rounded-xl border border-destructive/30 bg-destructive/5 p-6">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-destructive/15 text-destructive">
                  <XMarkIcon className="h-5 w-5" />
                </span>
                <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-destructive">
                  {t('aiJudgmentPage.promptComparisonWeakLabel')}
                </h3>
              </div>
              <blockquote
                className={`rounded-lg border border-destructive/20 bg-background/60 p-4 text-sm leading-relaxed text-foreground/85 ${promptFontClass}`}
              >
                {t('aiJudgmentPage.promptComparisonWeakPrompt')}
              </blockquote>
              <ul className="space-y-2 text-sm leading-7 text-muted-foreground">
                {WEAK_PROMPT_ISSUE_KEYS.map((key) => (
                  <li key={key} className="flex gap-2">
                    <span
                      aria-hidden
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive"
                    />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4 rounded-xl border border-accent/40 bg-accent/5 p-6">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <CheckIcon className="h-5 w-5" />
                </span>
                <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-accent">
                  {t('aiJudgmentPage.promptComparisonStrongLabel')}
                </h3>
              </div>
              <blockquote
                className={`rounded-lg border border-accent/20 bg-background/60 p-4 text-sm leading-relaxed text-foreground/85 ${promptFontClass}`}
              >
                {t('aiJudgmentPage.promptComparisonStrongPrompt')}
              </blockquote>
              <ul className="space-y-2 text-sm leading-7 text-muted-foreground">
                {STRONG_PROMPT_WIN_KEYS.map((key) => (
                  <li key={key} className="flex gap-2">
                    <span
                      aria-hidden
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                    />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <div aria-hidden className="h-px bg-border/50" />

        <section className="space-y-8" aria-labelledby="principles-heading">
          <div className="space-y-3">
            <h2
              id="principles-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('aiJudgmentPage.principlesTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('aiJudgmentPage.principlesDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3">
            {AI_PRINCIPLE_CARDS.map((card) => {
              const Icon = card.icon
              return (
                <div
                  key={card.titleKey}
                  className="px-6 py-5 md:py-2 md:border-s md:border-border/50 md:first:border-s-0 space-y-3"
                >
                  <h3 className="flex items-baseline gap-2 text-lg font-semibold text-foreground">
                    <Icon
                      aria-hidden
                      className="h-4 w-4 self-center text-accent/70"
                    />
                    {t(card.titleKey)}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {t(card.descriptionKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        <div aria-hidden className="h-px bg-border/50" />

        <section className="space-y-7" aria-labelledby="risk-heading">
          <div className="space-y-3">
            <h2
              id="risk-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('aiJudgmentPage.riskTitle')}
            </h2>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2">
            {AI_RISK_COLUMNS.map((column, index) => {
              const isLeftCol = index % 2 === 0
              return (
                <li
                  key={column.titleKey}
                  className={cn(
                    'px-1 py-6 md:px-7 md:py-7 space-y-2',
                    index < AI_RISK_COLUMNS.length - 1 &&
                      'border-b border-border/60',
                    index === AI_RISK_COLUMNS.length - 2 && 'md:border-b-0',
                    isLeftCol && 'md:border-e md:border-border/60'
                  )}
                >
                  <h3 className="text-base md:text-lg font-semibold text-foreground leading-snug">
                    {t(column.titleKey)}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(column.descriptionKey)}
                  </p>
                </li>
              )
            })}
          </ul>
        </section>

        <IntentRelatedLinks
          source="using_ai"
          links={[
            {
              to: '/faq#can-i-use-llms-chatgpt-gemini-claude',
              label: t('aiJudgmentPage.faqCardTitle'),
            },
            {
              to: '/xp-lab-feedback',
              label: t('intentResources.feedbackTitle'),
            },
            {
              to: '/take-home-assignments',
              label: t('intentResources.takeHomeAssignmentsTitle'),
            },
          ]}
        />
      </div>
    </div>
  )
}
